import template from './search-page.component.html';
import { EventCodes } from '../../../../App/model/event-codes';

export default {
	bindings: {
		contentWidgetId: '@',
		viewType: '<',
		filterType: '<',
		filterId: '<',
		navigationId: '<',
		extraFilter: '<',
	},
	controller: MxSearchPageContentCtrl,
	controllerAs: 'vm',
	template: template,
};

MxSearchPageContentCtrl.$inject = [
	'$timeout',
	'$scope',
	'$q',
	'$element',
	'mx.internationalization',
	'mx.shell.ContextService',
	'mx.shell.EventService',
	'mx.shell.SidePanelService',
	'mx.app.DeviceService',
	'mx.SolutionBuilderAgent.mxColumnDefinitionService',
	'mx.SolutionBuilderAgent.ContentWidgetService',
	'mx.shell.SearchProviderService',
	'mx.shell.KeyboardNavigationService',
	'mx.SolutionBuilderAgent.DataModelService',
	'mx.SolutionBuilderAgent.PersonalizationService',
	'mx.SolutionBuilderAgent.SearchQueryService',
	'mx.shell.Config',
	'mx.shell.NotificationService',
	'mx.SolutionBuilderAgent.ContextSelectionService',
	'mx.shell.SideLayout',
	'mx.shell.ActionManagerService',
	'mx.shell.AriaService',
	'mx.SolutionBuilderAgent.GroupingPanelService',
	'mx.shell.AppsService',
	'mx.SolutionBuilderAgent.SchemaService',
];

export function MxSearchPageContentCtrl(
	$timeout,
	$scope,
	$q,
	$element,
	i18n,
	contextService,
	eventService,
	sidePanelService,
	deviceService,
	columnDefinition,
	contentWidgetService,
	searchService,
	keyboardNavigationService,
	dataModelService,
	personalizationService,
	searchQueryService,
	mxConfigService,
	notificationService,
	contextSelectionService,
	sideLayoutService,
	actionManagerService,
	ariaService,
	groupingPanelService,
	appsService,
	schemaService
) {
	const vm = this;
	let _refreshInterval;
	let subscribers = [];
	let autoRefreshEnabled = false;
	const MIN_REFRESH_INTERVAL = 2;
	const AVAILABLE_ROW_HEIGHTS = [
		{
			size: 22,
			title: i18n.get('solutionBuilderAgent.common.high'),
		},
		{
			size: 30,
			title: i18n.get('solutionBuilderAgent.common.normal'),
		},
		{
			size: 38,
			title: i18n.get('solutionBuilderAgent.common.low'),
		},
	];

	const DEFAULT_ROW_HEIGHT = AVAILABLE_ROW_HEIGHTS[1].size;
	const DEFAULT_MAX_EXPORT_ROWS = 65000;

	const freeDataSourceDefer = $q.defer();

	vm.onViewSelect = onViewSelect;
	vm.getIconName = getIconName;
	vm.openAdvancedSearch = openAdvancedSearch;
	vm.export = exportData;
	vm.search = search;
	vm.onRefreshClick = onRefreshClick;

	vm.$onInit = function () {
		vm.loaded = false;
		vm.uiToken = 'uiToken:search-page-content';
		vm.context = searchService.currentContext();

		registerHotkeys();

		contextSelectionService.enableReactionOnContextSelection(true, $scope);

		if (!vm.contentWidgetId) {
			vm.loaded = true;
			mx.console.warn(i18n.get('solutionBuilderAgent.errors.search_page_content_is_not_provided'));
			return;
		}

		contextService.val('SolutionBuilderAgent.ContentWidgetId', vm.contentWidgetId);
		contextService.val('globalUiToken', vm.uiToken);

		enableSubscribers();
		loadContent().then(() => initQueries());
	};

	async function initQueries() {
		const queries = ((await searchQueryService.getQueries(vm.context)) || []).filter((query) => !query.showInNavigation);
		vm.showQueryFilterButton = queries.length > 0;
		vm.showQueryFilters = queries.some((query) => query.__active);

		eventService.subscribeTo(
			mx.SolutionBuilderAgent.Events.RunSearchQuery,
			(event, id) => {
				queries.forEach((query) => {
					const active = query.id === id;
					query.__active = active;

					if (active && vm.showQueryFilterButton) {
						vm.showQueryFilters = true;
					}

					if (active) {
						eventService.dispatchEvent(EventCodes.TrackTelemetry, {
							eventName: 'Search filter applied from filter panel',
							sender: `${vm.viewParameters.id}_${query.id}`,
							senderTitle: `${vm.viewParameters.name}_${query.title}`,
							contextTypeId: vm.viewParameters.entities.map((entity) => entity.id),
							contextTypeTitle: vm.viewParameters.entities.map((entity) => entity.name),
						});
					}
				});
			},
			$scope
		);
	}

	vm.$onDestroy = function () {
		freeDataSourceDefer.resolve(true);
		if (_refreshInterval) {
			clearInterval(_refreshInterval);
		}
		unregisterHotkeys();
		subscribers.forEach((unsubscribe) => unsubscribe());
		if (groupingPanelService.isActive()) {
			groupingPanelService.close();
		}
	};

	Object.defineProperty(vm, 'views', {
		get: function () {
			const views = vm.viewInfos.map(function (view) {
				return {
					label: view.Title,
					onItemClick: function () {
						vm.currentViewDropDownIcon = vm.getIconName(view.Type);
						vm.onViewSelect(view);
					},
				};
			});

			if (!angular.equals(views, vm._views)) {
				vm._views = views;
			}
			return vm._views;
		},
	});

	Object.defineProperty(vm, 'isMobile', {
		get: function () {
			return deviceService.screenExtraSmall();
		},
	});

	Object.defineProperty(vm, 'isCustomFormEnabled', {
		get: function () {
			return vm.view !== mx.SolutionBuilderAgent.ContentWidgetViewType.Grid;
		},
	});

	let view;
	Object.defineProperty(vm, 'view', {
		get: function () {
			return view;
		},
		set: function (selectedView) {
			view = selectedView;
			vm.serviceContextInfo = {
				widgetId: vm.contentWidgetId,
				viewName: vm.view.Key,
			};
		},
	});

	return vm;

	function enableSearchPageDataChangeSubscriber() {
		const _cleanSubscrReloads = [];
		let _cleanReloadTimeout,
			_reloadDataCalled = false;
		eventService.subscribeTo(
			mx.SolutionBuilderAgent.Events.SearchPageDataChanged,
			function (ev, eventParams) {
				if ((eventParams || {}).instantRefresh) {
					_reloadData();
					return;
				}

				// Performance optimization:
				// Here we need to postpone Grid-refreshing for a while to avoid browser-freezing
				// because browser is doing another operations (creating, saving, removing, ... some action after which we need to reload data).

				_reloadDataCalled = false;

				// Wait while new item is created (or similar action) and preview is shown...
				_cleanSubscrReloads.push(
					eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.ViewDataLoaded, function () {
						window.setTimeout(_reloadData, 500);
					})
				);
				// OR wait for closing SidePanel...
				_cleanSubscrReloads.push(
					eventService.subscribeOnce(mx.shell.EventCodes.SidePanelVisibility, function (_, visible) {
						if (!visible) {
							window.setTimeout(function () {
								if (!sideLayoutService.visible) {
									_reloadData();
								}
							}, 1000);
						}
					})
				);

				// OR wait for Wizard Closing...
				_cleanSubscrReloads.push(eventService.subscribeOnce(mx.shell.EventCodes.WizardClose, _reloadData));

				// OR wait if object is deleted...
				_cleanSubscrReloads.push(eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.ObjectDataDeleted, _reloadData));

				// BUT not longer then 3 seconds
				_cleanReloadTimeout = window.setTimeout(_reloadData, 3000);
			},
			$scope
		);

		function _reloadData() {
			if (_reloadDataCalled) {
				return;
			}
			_reloadDataCalled = true;

			$scope.$evalAsync(function () {
				vm.viewParameters.data = undefined;
				reload();
			});

			_cleanReloadProcess();
		}

		function _cleanReloadProcess() {
			_cleanSubscrReloads.forEach(function (cleaner) {
				if (cleaner) {
					cleaner();
				}
			});
			if (_cleanReloadTimeout) {
				window.clearTimeout(_cleanReloadTimeout);
			}
		}

		$scope.$on('$destroy', function () {
			_cleanReloadProcess();
		});
	}

	function enableSubscribers() {
		enableSearchPageDataChangeSubscriber();

		eventService.subscribeTo(
			mx.SolutionBuilderAgent.Events.SynchronizeWidget,
			function () {
				loadContent();
			},
			$scope
		);

		eventService.subscribeTo(
			mx.shell.EventCodes.Search,
			function (e, searchOptions = {}) {
				if (
					vm.uiToken === searchOptions.uiToken &&
					vm.context === searchOptions.context &&
					vm.viewParameters?.dataQueryList === searchOptions.dataQueryList &&
					searchOptions.keyword !== vm.searchText
				) {
					vm.searchText = searchOptions.keyword;
				}
			},
			$scope
		);

		enableGroupingSubscribers();
	}

	function enableGroupingSubscribers() {
		eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.SearchPageReloaded, function (e, { dataQueryList }) {
			if (!dataQueryList.groupingPreprocessor || vm.viewParameters.dataQueryList !== dataQueryList) {
				return;
			}

			if (dataQueryList.groupingPreprocessor.isActive()) {
				groupingPanelService.open(dataQueryList.groupingPreprocessor);
			}

			dataQueryList.groupingPreprocessor.addChangedHandler(() => {
				if (dataQueryList.groupingPreprocessor.isActive() && !groupingPanelService.isActive()) {
					groupingPanelService.open(dataQueryList.groupingPreprocessor);
				} else if (!dataQueryList.groupingPreprocessor.isActive() && groupingPanelService.isActive()) {
					groupingPanelService.close();
				}
			});

			if (vm.groupingPanelSubscriber) {
				const subscriberIndex = subscribers.indexOf(vm.groupingPanelSubscriber);
				vm.groupingPanelSubscriber();
				if (subscriberIndex >= 0) {
					subscribers.splice(subscriberIndex, 1);
				}
			}
			vm.groupingPanelSubscriber = eventService.subscribeTo(
				mx.shell.EventCodes.ExtraPanelClosed,
				() => {
					if (vm.viewParameters.dataQueryList.groupingPreprocessor.isActive()) {
						vm.viewParameters.dataQueryList.groupingPreprocessor.resetGrouping();
					}
				},
				$scope
			);
			subscribers.push(vm.groupingPanelSubscriber);
		});
	}

	function registerHotkeys() {
		$scope.$on('$destroy', function cleanUp() {
			$(window).off('keydown', onKeyDown);
		});

		$(window).on('keydown', onKeyDown);

		let switchButtons = $('.mx-search-page--switch-view-buttons:visible');

		if (switchButtons) {
			_registerSwitchViewsKey();
		}

		function _registerSwitchViewsKey() {
			keyboardNavigationService.registerKey({
				nameInTemplate: 'hintKey',
				hint: '⇑+v',
				scope: $scope,
				key: 'alt+v',
				description: i18n.get('solutionBuilderAgent.layouts.search-page.search-page-content.changeView'),
				callback: function () {
					$timeout(function () {
						$('.mx-search-page--switch-view-buttons:not([disabled]):visible').trigger('click');
					});
				},
			});
		}

		function onKeyDown() {
			switchButtons = switchButtons || $('.mx-search-page--switch-view-buttons:visible');

			if (!switchButtons.length) {
				keyboardNavigationService.unregisterKey('alt+shift+v');
			} else if (!keyboardNavigationService.getKeyOptions('alt+shift+v')) {
				_registerSwitchViewsKey();
			}
		}

		keyboardNavigationService.registerKey([
			{
				// Scope: $scope,
				key: 'alt+pageup',
				description: i18n.get('solutionBuilderAgent.components.mx-content-widget-view.previous'),
				callback: function () {
					$timeout(function () {
						const prevPageBtn = $element.find('.mx-workspace-common-paging-panel--pages--prev:visible:not([disabled])');
						if (prevPageBtn.length) {
							prevPageBtn.trigger('click');
							_handleAriaPageSwitching();
						}
					});
				},
			},
			{
				// Scope: $scope,
				key: 'alt+pagedown',
				description: i18n.get('solutionBuilderAgent.components.mx-content-widget-view.next'),
				callback: function () {
					$timeout(function () {
						const nextPageBtn = $element.find('.mx-workspace-common-paging-panel--pages--next:visible:not([disabled])');
						if (nextPageBtn.length) {
							nextPageBtn.trigger('click');
							_handleAriaPageSwitching();
						}
					});
				},
			},
			{
				// Scope: $scope,
				key: 'alt+up',
				description: i18n.get('solutionBuilderAgent.components.mx-content-widget-view.previousRow'),
				callback: function () {
					const currentRow = $element.find('mx-grid:visible .grid-row_active');

					if (!currentRow.length) {
						$element.find('mx-grid:visible .ui-grid-viewport .ui-grid-row:first-child').addClass('grid-row_active');
						return;
					}

					const previousRow = currentRow.prev();

					if (!previousRow.length) {
						return;
					}

					currentRow.removeClass('grid-row_active');
					previousRow.addClass('grid-row_active');
					$('.mx-search-page-grid')[0].focus();
				},
			},
			{
				// Scope: $scope,
				key: 'alt+down',
				description: i18n.get('solutionBuilderAgent.components.mx-content-widget-view.nextRow'),
				callback: function () {
					const currentRow = $element.find('mx-grid:visible .grid-row_active');

					if (!currentRow.length) {
						$element.find('mx-grid:visible .ui-grid-viewport .ui-grid-row:first-child').addClass('grid-row_active');
						return;
					}

					const nextRow = currentRow.next();

					if (!nextRow.length) {
						return;
					}

					currentRow.removeClass('grid-row_active');
					nextRow.addClass('grid-row_active');
					$('.mx-search-page-grid')[0].focus();
				},
			},
			{
				key: 'enter',
				callback: function () {
					if ($(document.activeElement).is('.mx-search-page-grid')) {
						const currentRow = $element.find('mx-grid:visible .grid-row_active');
						currentRow.click();
					}
				},
			},
		]);
	}

	function unregisterHotkeys() {
		keyboardNavigationService.unregisterKey('alt+down');
		keyboardNavigationService.unregisterKey('alt+up');
		keyboardNavigationService.unregisterKey('alt+pagedown');
		keyboardNavigationService.unregisterKey('alt+pageup');
	}

	function setOnChangeCallbacks() {
		_.forOwn(vm.viewParameters.dataModel.$calcProperties, function (calcProperty) {
			if (
				calcProperty.descriptor.Path.indexOf(vm.viewParameters.dataQuery.Name + '.') === 0 &&
				calcProperty.descriptor.Direction === mx.SolutionBuilderAgent.DataModelPropertyDirection.IN
			) {
				calcProperty.onChange('SEARCH_PAGE_RELOAD', function () {
					eventService.dispatchEvent(mx.SolutionBuilderAgent.Events.SearchPageDataChanged, { instantRefresh: true });
				});
			}
		});
	}

	async function loadContent() {
		vm.loaded = false;
		vm.app = await appsService.getCurrentApplication();

		return contentWidgetService
			.getHtml(vm.contentWidgetId)
			.then(async function (widget) {
				let customFormView;
				const i18nSPViews = i18n.get('solutionBuilderAgent.layouts.search-page.views');

				// For Mobile: set 'List' as default view if it exists.
				if (deviceService.screenMobile()) {
					widget.Template.Views.forEach(function (v) {
						if (v.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.List && widget.ListViewEnabled) {
							vm.view = v;
						}
					});
				}

				vm.contentWidgetName = widget.Name;

				let hasBoardView = false;
				vm.viewInfos = widget.Template.Views.filter(function (view) {
					hasBoardView = hasBoardView || (view.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.Board && widget.BoardViewEnabled);

					return (
						(view.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.List && widget.ListViewEnabled) ||
						(view.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.Tile && widget.TileViewEnabled) ||
						(view.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.Board && widget.BoardViewEnabled)
					);
				}).map(function (view) {
					return {
						Type: view.Type,
						Title: view.Title,
						Template: view.Template,
					};
				});

				if (widget.CalendarViewEnabled) {
					vm.viewInfos.push({
						Type: mx.SolutionBuilderAgent.ContentWidgetViewType.Calendar,
						Title: i18nSPViews.calendar,
					});
				}

				if (widget.GridViewEnabled) {
					vm.viewInfos.push({
						Type: mx.SolutionBuilderAgent.ContentWidgetViewType.Grid,
						Title: i18nSPViews.grid,
					});
				}

				// If there is no designed Board view but BoardView is enabled than show board with static items
				if (widget.BoardViewEnabled && !hasBoardView) {
					vm.viewInfos.push({
						Type: mx.SolutionBuilderAgent.ContentWidgetViewType.Board,
						Title: i18nSPViews.board,
						Template: null,
					});
				}

				// Gets an info about the default view in case it wasn't set before.
				let _defaultViewType = contentWidgetService.getDefaultViewType(vm.contentWidgetId);
				let selectedViewType;

				if (_defaultViewType === null) {
					_defaultViewType = widget.DefaultView;
				}

				vm.viewInfos.forEach(function (v) {
					if (v.Type === _defaultViewType) {
						_defaultViewType = v;
					}

					if (v.Type === vm.viewType) {
						selectedViewType = v;
					}
				});

				vm.view = selectedViewType || _defaultViewType;

				customFormView = _.find(widget.Template.Views, { Type: mx.SolutionBuilderAgent.ContentWidgetViewType.CustomSearchForm }) || null;

				if (customFormView) {
					vm.customFormView = customFormView.Template || '';
					vm.customFormServiceContextInfo = {
						widgetId: vm.contentWidgetId,
						viewName: customFormView.Key,
					};
				}

				autoRefreshEnabled = widget.AutoRefreshInGridEnabled;

				vm.viewParameters = {
					isSearchPage: true,
					filterExpression: widget.FilterExpression,
					isMultiFragment: widget.IsMultiFragment,
					dataQueryId: widget.DataQueryId,
					dataModelOptions: widget.Template.DataModelExtensions,
					nonVisualTemplate: widget.Template.NonVisualTemplate,
					id: vm.contentWidgetId,
					name: widget.Name,
					templateId: widget.Template.Id,
					entityType: widget.EntityType,
					entity: widget.Entity,
					quickFilters: Boolean(widget.RedFilter || widget.YellowFilter || widget.GreenFilter),
					redFilter: widget.RedFilter,
					yellowFilter: widget.YellowFilter,
					greenFilter: widget.GreenFilter,
					quickFilterTitles: {
						red: widget.RedTitle,
						yellow: widget.YellowTitle,
						green: widget.GreenTitle,
					},
					calendar: {
						titleKey: widget.CalendarSubjectField || '_displayName',
						descriptionKey: widget.CalendarDescriptionField,
						startDateKey: widget.CalendarStartDateField,
						defaultView: widget.CalendarDefaultView,
						endDateKey: widget.CalendarEndDateField,
						wholeDayKey: widget.CalendarEventIsWholeDayColumn,
						readonlyKey: widget.CalendarEventIsReadOnlyColumn,
						backgroundKey: widget.CalendarEventIsBackgroundColumn,
						showHolidays: widget.CalendarShowHolidays,
						groupByExtraDimension: widget.GroupByExtraDimension,
						calendarExtraDimensionRelation: widget.CalendarExtraDimensionRelation,
						calendarExtraDimensionImageColumn: widget.CalendarExtraDimensionImageColumn,
						calendarItemColorColumn: widget.CalendarItemColorColumn,
						showDayView: Boolean(widget.ShowDayView),
						showWeekView: Boolean(widget.ShowWeekView),
						showMonthView: Boolean(widget.ShowMonthView),
					},
					board: {
						defaultHorizontalGroupingField: widget.DefaultBoardGroupingAttribute,
						dragAndDropEnabled: widget.BoardDragAndDropEnabled,
					},
					uiToken: vm.uiToken,
					structureNodeId: Boolean(vm.filterId) && vm.filterType === 'structure' ? vm.filterId : undefined,
					filterId: vm.filterId,
					filterType: vm.filterType,
				};

				if (widget.LoadOnlyFilteredData && vm.filterType === 'nofilter') {
					let loadOnlyFilteredData = true;
					Object.defineProperty(vm.viewParameters, 'loadOnlyFilteredData', {
						set: function (newValue) {
							if (!newValue) {
								delete vm.viewParameters.data;
								search();
							}
							loadOnlyFilteredData = newValue;
						},
						get: function () {
							return loadOnlyFilteredData;
						},
					});
					vm.viewParameters.data = [];
					const subscriber = eventService.subscribeTo(
						mx.shell.EventCodes.Search,
						function () {
							loadOnlyFilteredData = false;
							subscriber();
						},
						$scope
					);
				}

				vm.currentViewDropDownIcon = vm.getIconName(vm.view.Type);

				const dataQuery = await columnDefinition.getGridDefinitionById(widget.DataQueryId, vm.contentWidgetId);

				vm.viewParameters.service = dataQuery.Service;
				vm.viewParameters.serviceInfo = dataQuery.ServiceInfo;
				vm.viewParameters.dataQuery = dataQuery;

				const typesInfo = await Promise.all((widget.Entities || []).map((entity) => schemaService.getType(entity.Name)));
				vm.viewParameters.entities = await Promise.all(
					typesInfo.map(async (info, index) => {
						if (!info) {
							if (dataQuery.serviceInfo) {
								const name = 'service_' + dataQuery.ServiceInfo.Name;
								return {
									id: 'service_' + dataQuery.ServiceInfo.Id,
									name: name,
									displayName: (await dataQuery.getRecordTypeDisplayString()) || name,
								};
							} else {
								return {
									id: widget.Entities[index].Id || widget.Entities[index].Name,
									name: widget.Entities[index].Name,
									displayName: widget.Entities[index].DisplayName || widget.Entities[index].Name,
								};
							}
						} else {
							return {
								id: info.Id,
								name: info.InternalName,
								displayName: info.DisplayName,
							};
						}
					})
				);

				vm.viewParameters.entityTypes = vm.viewParameters.entities.map((entity) => entity.name);

				// Init actions
				if (dataQuery.serviceInfo) {
					vm.viewParameters.entity = 'service_' + dataQuery.Service;
				}
				const isExportSupported = !dataQuery.ServiceInfo || dataQuery.isExportSupported();
				return isExportSupported
					? actionManagerService.isExportAllAvailable(vm.viewParameters.entityTypes).then(function (available) {
							vm.showExportButton = available;
					  })
					: $q.when();
			})
			.then(function () {
				const dataModelOptions = vm.viewParameters.dataModelOptions
					? JSON.parse(vm.viewParameters.dataModelOptions)
					: {
							Sources: [],
							Properties: [],
					  };

				dataModelOptions.dataQueryId = vm.viewParameters.dataQueryId;
				dataModelOptions.widgetId = vm.viewParameters.id;

				const ctx = {
					WidgetId: dataModelOptions.widgetId,
					NavigationId: (vm.navigationId || '').toLowerCase(),
				};
				const staticData = { Context: ctx };

				let sharedContext = contextService.val('ApplicationSharedContext');
				if (!sharedContext) {
					contextService.val('ApplicationSharedContext', (sharedContext = {}));
				}
				staticData.Context[mx.SolutionBuilderAgent.DataModelSystemProperty.Shared] = sharedContext;
				return dataModelService.loadDataModel(dataModelOptions, staticData, undefined, freeDataSourceDefer.promise);
			})
			.then(function (dataModel) {
				vm.viewParameters.dataModel = dataModel;
				vm.dataModel = dataModel;
				setOnChangeCallbacks();

				if (autoRefreshEnabled) {
					return personalizationService.getRefreshInterval(vm.viewParameters.dataQueryId).then(addAutoRefreshConfig);
				}
			})
			.then(function () {
				return contentWidgetService.isCustomizable({ isSearchPage: true }).then(function (isCustomizable) {
					if (isCustomizable) {
						vm.isCustomizable = true;
						vm.configure = function () {
							contentWidgetService.openConfigurePreview(vm.viewParameters);
						};
					}
				});
			})
			.then(function () {
				let rowHeight = DEFAULT_ROW_HEIGHT;
				Object.defineProperty(vm.viewParameters, 'rowHeight', {
					get: function () {
						return rowHeight;
					},
					set: function (newRowHeight) {
						rowHeight = newRowHeight || DEFAULT_ROW_HEIGHT;
						personalizationService.setRowHeight(rowHeight);
					},
				});

				vm.availableRowHeights = vm.view.Type === mx.SolutionBuilderAgent.ContentWidgetViewType.Grid ? AVAILABLE_ROW_HEIGHTS : [];

				return personalizationService.getRowHeight().then(function (savedRowHeight) {
					rowHeight = savedRowHeight === undefined ? rowHeight : savedRowHeight;
				});
			})
			.then(function () {
				let rowColoringEnabled = true;

				Object.defineProperty(vm.viewParameters, 'rowColoringEnabled', {
					get: function () {
						return typeof rowColoringEnabled === 'undefined' ? true : rowColoringEnabled;
					},
					set: function (newValue) {
						rowColoringEnabled = newValue;
						personalizationService.setRowsColoringAttribute('rowColoringEnabled', rowColoringEnabled);
					},
				});

				return personalizationService.getRowsColoring().then(function (coloring) {
					rowColoringEnabled = coloring.enabled === undefined ? rowColoringEnabled : coloring.enabled;
				});
			})
			.then(function () {
				return personalizationService.getQuickFilters(vm.viewParameters.dataQueryId).then(function (savedQuickFilters) {
					let quickFilters = savedQuickFilters || [];

					Object.defineProperty(vm.viewParameters, 'quickFilters', {
						get: function () {
							return quickFilters;
						},
						set: function (newValue) {
							quickFilters = newValue;
							personalizationService.setQuickFilters(vm.viewParameters.dataQueryId, newValue);
						},
					});
				});
			})
			.then(
				function () {
					vm.loaded = true;

					// Localization Strings are loading very, very slow (up to 40 seconds)
					// We show special message for this exceptional case.
					const localizationDataQueryId = 'b2ae1485-6360-cdb3-4806-08d61fcecd2a';
					if (vm.viewParameters.dataQueryId === localizationDataQueryId) {
						const localizationLoader = window.setTimeout(function () {
							$element.append('<div class="mx-search-page--loading-text">' + i18n.get('solutionBuilderAgent.localization-strings-loading-msg') + '</div>');
						}, 5000);
						eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.SearchPageReloaded, function () {
							window.clearTimeout(localizationLoader);
							$element.find('.mx-search-page--loading-text').remove();
						});
					}
				},
				function (error) {
					vm.errorMessage = error && (error.message || error);
					vm.errorTitle = error && error.title;
					$q.reject(error);
				}
			);
	}

	function addAutoRefreshConfig(interval) {
		vm.viewParameters.autoRefreshConfig = {
			_enable: !isNaN(interval) && interval >= MIN_REFRESH_INTERVAL,
			_interval: !isNaN(interval) && interval >= MIN_REFRESH_INTERVAL ? interval : MIN_REFRESH_INTERVAL,
		};

		Object.defineProperty(vm.viewParameters.autoRefreshConfig, 'interval', {
			set: function (newInterval) {
				if (!isNaN(newInterval) && newInterval >= MIN_REFRESH_INTERVAL) {
					this._interval = newInterval;
					this.isError = false;
				} else {
					this.isError = true;
					this._interval = MIN_REFRESH_INTERVAL;
				}
				updateAutoRefreshSettings(this);
			},
			get: function () {
				return this._interval;
			},
		});

		Object.defineProperty(vm.viewParameters.autoRefreshConfig, 'enable', {
			set: function (enable) {
				this._enable = enable;
				updateAutoRefreshSettings(this);
			},
			get: function () {
				return this._enable;
			},
		});

		updateAutoRefreshSettings(vm.viewParameters.autoRefreshConfig);
	}

	function updateAutoRefreshSettings(refreshConfig) {
		if (_refreshInterval) {
			clearInterval(_refreshInterval);
		}

		if (refreshConfig.enable && refreshConfig.interval >= MIN_REFRESH_INTERVAL) {
			_refreshInterval = setInterval(reload, refreshConfig.interval * 60000);
		}

		personalizationService.saveRefreshInterval(refreshConfig.enable ? refreshConfig.interval : 0, vm.viewParameters.dataQueryId);
	}

	function openAdvancedSearch() {
		searchService.openAdvancedSearch({
			// TODO: better to pass vm.context  from here and not get in on that side
			// Context: vm.context,
			dataQueryList: vm.viewParameters.dataQueryList,
			uiToken: vm.uiToken,
		});
	}

	async function exportData() {
		const localizationDataQueryId = 'b2ae1485-6360-cdb3-4806-08d61fcecd2a';
		const dataQueryList = vm.viewParameters.dataQueryList;
		const maxPageSize = (mxConfigService.get('exportSource') || {}).maxRowSize || DEFAULT_MAX_EXPORT_ROWS;
		let totalCount = vm.viewParameters.totalCount;
		const dialogConfig = {
			id: 'ActionOnExecuteServiceOperation-exportAll',
			title: i18n.get('solutionBuilderAgent.common.export-all'),
			message: i18n.get('solutionBuilderAgent.common.export-all-max-size-exceeded'),
			actions: [
				{
					id: 'ok',
					isConfirmButton: true,
					title: i18n.get('solutionBuilderAgent.components.mx-localization-editor.ok'),
				},
				{
					id: 'cancel',
					notForSave: true,
					title: i18n.get('solutionBuilderAgent.components.mx-localization-editor.cancel'),
				},
			],
			notSaved: true,
			buttonsInActionZone: true,
		};

		if (!dataQueryList.data || !dataQueryList.data.length) {
			return;
		}

		vm.disableExportButton = true;

		trackTelemetry('All items exported');

		if (totalCount === -1) {
			totalCount = await dataQueryList.pagingPreprocessor.getDataCount();
		}

		if ((totalCount && totalCount > 0 && totalCount <= maxPageSize) || localizationDataQueryId === vm.viewParameters.dataQueryId) {
			return executeExportData();
		}

		const answer = await sidePanelService.showConfirmationDialog(dialogConfig);
		if (answer === 'ok') {
			return executeExportData();
		}
		vm.disableExportButton = false;
	}

	function executeExportData() {
		vm.disableExportButton = true;
		$timeout(function () {
			if (vm.disableExportButton) {
				notificationService.warning(i18n.get('solutionBuilderAgent.common.export-all-executing'));
			}
		}, 3000);

		return vm.viewParameters.dataQueryList.dataQueryExecutor.executeExportData().finally(function () {
			vm.disableExportButton = false;
		});
	}

	function trackTelemetry(eventName) {
		eventService.dispatchEvent(EventCodes.TrackTelemetry, {
			eventName,
			sender: vm.viewParameters.id,
			senderTitle: vm.viewParameters.name,
			contextTypeId: vm.viewParameters.entities.map((entity) => entity.id),
			contextTypeTitle: vm.viewParameters.entities.map((entity) => entity.name),
		});
	}

	function onRefreshClick() {
		trackTelemetry('Refresh list clicked');
		reload();
	}

	function reload() {
		vm.viewParameters.data = undefined;
		// check if dialog/wizard was loaded faster than List of data  (e.g. when open dialog by URL)
		if (vm.viewParameters.dataQueryList && vm.viewParameters.dataQueryList.debouncedReload) {
			vm.viewParameters.dataQueryList.debouncedReload(true);
		}
	}

	function search(searchText, dontHideCustomForm, runByEnter) {
		const currentSearch = searchService.getCurrentSearchPageSearchOptions();
		vm.searchText = searchText;
		currentSearch.keyword = searchText;
		currentSearch.uiToken = vm.uiToken;
		currentSearch.dontHideCustomForm = vm.dontHideCustomForm;

		if (runByEnter) {
			_handleAriaSearch();
		}

		trackTelemetry('Keyword search executed');
		searchService.runSearch(currentSearch);
	}

	function _handleAriaSearch() {
		const jumpToResult = ariaService.createPoliteFocusSettingFn('.mx-content-widget-view--no-filtered-data--clear, .mx-search-page-grid');
		ariaService.readMessage(i18n.get('solutionBuilderAgent.layouts.search-page.search-page-content.ARIA-searchInProgress'));
		eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.SearchPageReloaded, async () => {
			const totalCount = vm.viewParameters.totalCount > -1 ? vm.viewParameters.totalCount : i18n.get('components.mx-datasource-paging-panel.many');
			await ariaService.readMessage(i18n.getFormatted('solutionBuilderAgent.layouts.search-page.search-page-content.ARIA-foundItems', totalCount));
			jumpToResult();
		});
	}

	function _handleAriaPageSwitching() {
		eventService.subscribeOnce(mx.SolutionBuilderAgent.Events.SearchPageReloaded, () => {
			setTimeout(() => {
				const gridEl = $element.find('.mx-search-page-grid');
				gridEl.trigger('focus');
				ariaService.readMessage(gridEl.find('[role=grid]').attr('aria-label'));
			}, 600);
		});
	}

	function getIconName(type) {
		return (
			(type === mx.SolutionBuilderAgent.ContentWidgetViewType.Grid && 'grid_on') ||
			(type === mx.SolutionBuilderAgent.ContentWidgetViewType.List && 'view_list') ||
			(type === mx.SolutionBuilderAgent.ContentWidgetViewType.Tile && 'view_quilt') ||
			(type === mx.SolutionBuilderAgent.ContentWidgetViewType.Calendar && 'date_range') ||
			(type === mx.SolutionBuilderAgent.ContentWidgetViewType.Board && 'view_week')
		);
	}

	function onViewSelect(selectedView) {
		const viewType = selectedView.Type;
		const contentWidgetId = vm.contentWidgetId;

		vm.availableRowHeights = viewType === mx.SolutionBuilderAgent.ContentWidgetViewType.Grid ? AVAILABLE_ROW_HEIGHTS : [];
		vm.view = selectedView;

		if (contentWidgetId !== null) {
			contentWidgetService.setDefaultViewType(contentWidgetId, viewType);
		}

		contextService.selectedObjects.forEach(function (item) {
			delete item.__isSelected;
		});
		contextService.selectedObjects = [];
		vm.viewParameters.data = undefined;
		searchService.resetSearchParameters();

		enableGroupingSubscribers();
	}
}
